import React from "react";
import { useStoryItemSql } from "../../../hooks/useStoryItemSql";
import { storySqlDialogCopy as copy } from "./StorySqlDialog.copy";
import {
  DetailHead,
  DetailTitle,
  KindChip,
  Note,
  PaneBox,
  PaneButton,
  PaneCode,
  PaneHead,
  PaneHint,
  PaneTag,
  PaneTitle,
} from "./StorySqlDialog.styles";

interface StorySqlPaneProps {
  storyId: string;
  chartId: string;
  title: string;
  kind?: string;
  /** Show the chart's title and kind above the SQL (side by side compare). */
  showHead?: boolean;
  copiedKey: string | null;
  onCopy: (sql: string, key: string) => void;
}

/** One chart's readable SQL: fetches it, shows each statement in a dark pane with a copy button. */
export const StorySqlPane: React.FC<StorySqlPaneProps> = ({
  storyId,
  chartId,
  title,
  kind,
  showHead = false,
  copiedKey,
  onCopy,
}) => {
  const sql = useStoryItemSql(storyId, chartId, true);
  return (
    <section aria-label={title} style={{ display: "flex", flexDirection: "column", gap: "0.75rem", minHeight: 0 }}>
      {showHead ? (
        <DetailHead>
          <DetailTitle>{title}</DetailTitle>
          {kind ? <KindChip>{kind}</KindChip> : null}
        </DetailHead>
      ) : null}
      {sql.state === "error" ? (
        <Note role="alert">{sql.errorMessage}</Note>
      ) : null}
      {sql.state === "loading" || sql.state === "idle" ? (
        <Note role="status">{copy.loading}</Note>
      ) : null}
      {sql.state === "ready"
        ? sql.data?.statements.map((statement) => {
            const key = `${chartId}:${statement.key}`;
            return (
              <PaneBox key={key}>
                <PaneHead>
                  <PaneTag>{copy.readableTag}</PaneTag>
                  <PaneTitle>
                    {statement.output
                      ? `${copy.readablePane}: ${statement.output}`
                      : copy.readablePane}
                  </PaneTitle>
                  <PaneHint>{copy.readableHint}</PaneHint>
                  <span style={{ flex: 1 }} />
                  <PaneButton type="button" onClick={() => onCopy(statement.sql, key)}>
                    {copiedKey === key ? copy.copied : copy.copy}
                  </PaneButton>
                </PaneHead>
                <PaneCode tabIndex={0} aria-label={`${copy.readablePane}: ${title}`}>
                  {statement.sql}
                </PaneCode>
              </PaneBox>
            );
          })
        : null}
      {sql.state === "ready" &&
      (sql.data?.message || sql.data?.statements.length === 0) ? (
        <Note>{sql.data?.message ?? copy.empty}</Note>
      ) : null}
    </section>
  );
};
